import { computed, ref } from "vue"
import { toast } from "vue-sonner"
import { defineStore } from "pinia"
import { fetchRecordsBySchema } from "@/common/hasura/records"
import { client } from "@/init/apollo"
import { useSchemasStore } from "@/stores/schemas"

const PROMPT_SCHEMA_NAME = "Prompt"

/**
 * Substitute `{{key}}` tokens in `body` with the matching value from `vars`.
 * Exported so callers holding a prompt body directly (e.g. PromptBlock, which
 * picks a Prompt record by id) can fill placeholders without going through the
 * slug-keyed store.
 */
export function applyVars(body: string, vars?: Record<string, string>): string {
  if (!vars) return body
  let out = body
  for (const [key, value] of Object.entries(vars)) {
    // Coerce nullish to "" so a missing value empties the placeholder
    // rather than substituting the literal string "null"/"undefined".
    out = out.split(`{{${key}}}`).join(value ?? "")
  }
  return out
}

// Toast once per missing slug per session — a caller may hit the same slug
// several times and we don't want a toast each time.
const warnedSlugs = new Set<string>()

function warnMissing(slug: string) {
  if (warnedSlugs.has(slug)) return
  warnedSlugs.add(slug)
  console.warn(`[prompts] Missing AI prompt record: ${slug}`)
  toast.error(`AI prompt "${slug}" is not configured — ask an admin to create it.`)
}

/**
 * Module-level helper — callers use `await buildPrompt("ai-chat-system", { ... })`
 * without needing to know the store exists. The prompts load on first use, not
 * at boot: only an agent run needs them.
 *
 * Missing prompts return `""` and surface a toast (once per slug). Callers
 * that assemble a prompt should check for empty and skip the API call;
 * `useClaude.stream` does this automatically.
 */
export const buildPrompt = async (slug: string, vars?: Record<string, string>): Promise<string> => {
  const store = usePromptsStore()
  await store.fetch()
  return store.get(slug, vars)
}

export const usePromptsStore = defineStore("prompts", () => {
  const prompts = ref<Record<string, string>>({})
  const loading = ref(false)
  const initialized = ref(false)

  const slugs = computed(() => Object.keys(prompts.value))

  const get = (slug: string, vars?: Record<string, string>): string => {
    const body = prompts.value[slug]
    if (body == null) {
      warnMissing(slug)
      return ""
    }
    return applyVars(body, vars)
  }

  let warnedNoSchema = false
  // Prompts load on first use, so several agent prompts built at once share one fetch.
  let inflight: Promise<Record<string, string>> | null = null

  const fetch = (): Promise<Record<string, string>> => {
    if (initialized.value) return Promise.resolve(prompts.value)
    if (inflight) return inflight
    const schema = useSchemasStore().getByName("document", PROMPT_SCHEMA_NAME)
    if (!schema) {
      // Not marked initialized: the catalog may gain the schema on its next
      // check, and the next call should find it.
      if (!warnedNoSchema) toast.warning("AI prompts unavailable — Prompt schema not found")
      warnedNoSchema = true
      return Promise.resolve(prompts.value)
    }
    loading.value = true
    inflight = client
      .query({
        query: fetchRecordsBySchema,
        variables: { schemaId: schema.id },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const next: Record<string, string> = {}
        for (const record of data.records as any[]) {
          const content = (record.data as { content?: string } | null)?.content
          if (typeof content === "string" && record.slug) {
            next[record.slug] = content
          }
        }
        prompts.value = next
        initialized.value = true
        return prompts.value
      })
      .finally(() => {
        loading.value = false
        inflight = null
      })
    return inflight
  }

  const refetch = (): Promise<Record<string, string>> => {
    initialized.value = false
    return fetch()
  }

  return {
    prompts,
    loading,
    initialized,
    slugs,
    get,
    fetch,
    refetch,
  }
})
